"use client"

import NewMinicart from "@/components/MiniCart/new-minicart"
import { Newsletter } from "@/components/newsletter"
import { SocialLinks } from "@/components/SocialLinks"
import { useUserContext } from "@/context/user"
import { cn } from "@/lib/utils"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { useMemo } from "react"
import FooterLinksBlock from "./footerLinksBlock"
import { LogoIcon } from "./logo-icon"
import { AmexIcon } from "./payments-icon/amex"
import { BlueIcon } from "./payments-icon/blue-icon"
import { JCBIcon } from "./payments-icon/jcb"
import { MaIcon } from "./payments-icon/ma"
import { MasterCardIcon } from "./payments-icon/master-card"
import { PaypalIcon } from "./payments-icon/paypal"
import { SezzleIcon } from "./payments-icon/sezzle"
import { VisaIcon } from "./payments-icon/visa-icon"
import Features from "@/components/Features"

const Footer = () => {
  const [{ isSignedIn }] = useUserContext()
  const accountLink = useMemo(() => {
    if (isSignedIn) {
      return "/account-information"
    }

    return "/sign-in"
  }, [isSignedIn])

  const currentYear = new Date().getFullYear()
  const pathname = usePathname()

  if (pathname.includes("checkout")) {
    return null
  }

  const shopLinks = [
    {
      text: "Hummingbird Feeders",
      href: "/hummingbird-feeders",
    },
    {
      text: "Squirrels Proof Feeders",
      href: "/squirrel-proof-feeders",
    },

    {
      text: "Suet Bird Feeders",
      href: "/suet-bird-feeders",
    },
    {
      text: "Caged Bird Feeders",
      href: "/caged-bird-feeders",
    },
    {
      text: "Poles & Hangers",
      href: "/poles-hanger-hooks",
    },
  ]

  const brandLinks = [
    {
      href: "/about-us/",
      text: "About Kingsyard",
    },
    {
      href: "/reviews/",
      text: "Customer Reviews",
    },
    {
      href: "/blog/",
      text: (
        <span>
          <span>Blog</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            fill="#fff"
            viewBox="0 0 24 24"
            className="ml-2.5 inline-block"
          >
            <path d="M14 3h7v7h-2V6.41l-9.29 9.3-1.42-1.42L17.59 5H14z"></path>
            <path d="M5 5h7V3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7H5z"></path>
          </svg>
        </span>
      ),
      target: "_blank",
    },
    {
      href: "/social-responsibility/",
      text: "Social Responsibility",
    },
  ]

  const programLinks = [
    {
      href: "/rewards-program",
      text: "Rewards Program",
    },
    {
      href: "/wholesale-program/",
      text: "Wholesale Program",
    },
    {
      href: "/affiliate-program/",
      text: "Affiliate Program",
    },
    {
      href: "/recruit-free-product-tester",
      text: "Product Tester",
    },
  ]

  const helpSupportLinks = [
    {
      href: "/contact-us/",
      text: "Contact Us",
    },
    {
      href: "/help-faqs/",
      text: "Help & FAQs",
    },
    {
      href: "/warranty/",
      text: "Lifetime Warranty",
    },
    {
      href: "/shipping-return-policy/",
      text: "Shipping & Returns Policy",
    },
    {
      href: "/track-your-order/",
      text: "Track Your Order",
    },
  ]

  const renderFooterLinksBlock = (header, links) => (
    <FooterLinksBlock
      value={header}
      header={
        <span className="inline-block h-[60px]  font-poppins text-[18px] font-[600] uppercase leading-[60px] text-white lg_h-auto lg_text-[20px] lg_leading-[1.4]">
          {header}
        </span>
      }
      content={
        <ul className="flex min-w-[210px] flex-col gap-[20px] pb-[20px] font-sans text-[16px] leading-[1.4] text-white">
          {links.map((link, index) => {
            return (
              <li key={index}>
                <Link
                  href={link.href}
                  className="underline-offset-4 hover_font-[600] hover_underline"
                  target={link.target}
                >
                  {link.text}
                </Link>
              </li>
            )
          })}
        </ul>
      }
    />
  )

  return (
    <div>
      <Features />
      <footer
        data-cy="Footer-root"
        className={cn(
          "border-t-1",
          "border-solid",
          "border-[#DDDDDD]",
          "gap-[40px] lg_gap-y-[130px]",
          "grid",
          "leading-normal",
          "min-h-[15rem]",
          "my-0",
          "lg_pt-[80px]",
          "lg_pb-[60px]",
          "text-sm",
          "text-subtle",
          "w-full",
          "bg-primary",
          "text-white"
        )}
      >
        <div className="mx-auto flex w-full flex-col px-[17.5px] lg_max-w-[1720px] lg_flex-row lg_px-0 lg_pr-[55px]">
          <Newsletter className="block lg_hidden" />
          <SocialLinks className="mt-[30px] flex w-full justify-between px-[22px] lg_hidden" />
          <div className="hidden lg_mr-[190px] lg_block lg_w-[400px]">
            <div className="hidden lg_block">
              <LogoIcon />
            </div>
            <Newsletter className="hidden lg_block" />
            <div className={cn("mt-[60px]")}>
              <SocialLinks className="hidden lg_grid" />
            </div>
          </div>
          <div
            className={cn(
              "flex-1",
              "flex",
              "justify-between",
              "lg_border-none",
              "flex-col lg_flex-row",
              "pt-10 lg_pt-0"
            )}
          >
            <div className="border-t-1 border-[#ccc] lg_border-none">
              {renderFooterLinksBlock("SUPPORT", helpSupportLinks)}
            </div>
            {renderFooterLinksBlock("Brand", brandLinks)}
            {renderFooterLinksBlock("Programs", programLinks)}
            {renderFooterLinksBlock("Quick Shop", shopLinks)}
          </div>
        </div>

        {
          // privacy policy
        }
        <div className="mx-auto flex w-full max-w-[1720px] flex-col-reverse justify-between gap-4 border-t-1 border-solid border-[#A1C323] py-[20px] lg_flex-row lg_pb-[60px] lg_pt-[60px]">
          <div className="flex flex-col items-center  justify-between gap-[5px] font-sans text-[12px] leading-[1.4] lg_flex-row lg_gap-[15px]">
            <span>COPYRIGHT {currentYear} @ KINGSYARD Inc</span>
            <div className="flex gap-[15px]">
              {[
                {
                  href: "/privacy-policy",
                  text: "Privacy Policy",
                },
                {
                  href: "/terms-privacy",
                  text: "Terms of Use",
                },
              ].map((link, index) => (
                <Link
                  key={index}
                  href={link.href}
                  className="font-[600] underline"
                >
                  {link.text}
                </Link>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-[10px]">
            <PaypalIcon />
            <VisaIcon />
            <SezzleIcon />
            <MasterCardIcon />
            <AmexIcon />
            <BlueIcon />
            <JCBIcon />
            <MaIcon />
          </div>
        </div>
        <NewMinicart />
      </footer>
    </div>
  )
}

export default Footer
